/**
 * Utility functions for parsing and handling numeric input values
 */

/**
 * Parse a string input value to a number or null.
 * Returns null for empty strings, otherwise parses as float.
 *
 * @param value - The string value from an input field
 * @returns The parsed numeric value, or null if empty
 *
 * @example
 * parseNumericInput('') // returns null
 * parseNumericInput('42') // returns 42
 * parseNumericInput('3.14') // returns 3.14
 */
export function parseNumericInput(value: string): number | null {
  return value === "" ? null : parseFloat(value);
}

/**
 * Create a change handler for numeric inputs that parses the value and calls onChange with name and numeric value.
 *
 * @param name - The input field name
 * @param onChange - Callback to invoke with the field name and parsed numeric value (null when empty)
 * @returns A React change event handler
 */
export function createNumericChangeHandler(
  name: string,
  onChange: (name: string, value: number | null) => void
): (e: React.ChangeEvent<HTMLInputElement>) => void {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = parseNumericInput(e.target.value);
    onChange(name, numValue);
  };
}

/**
 * Create a blur handler for numeric inputs that parses the value and calls onBlur with name and numeric value.
 *
 * @param name - The input field name
 * @param onBlur - Callback to invoke with the field name and parsed numeric value (null when empty)
 * @returns A React focus event handler
 */
export function createNumericBlurHandler(
  name: string,
  onBlur: (name: string, value: number | null) => void
): (e: React.FocusEvent<HTMLInputElement>) => void {
  return (e: React.FocusEvent<HTMLInputElement>) => {
    const numValue = parseNumericInput(e.target.value);
    onBlur(name, numValue);
  };
}
